import { context, trace } from '@opentelemetry/api';
import { logger } from '@parrot-software/observability';

import type { PaymentMethodViewDto } from '@subscriptions/application/dtos';
import type { IPaymentMethodApiPort } from '@subscriptions/application/ports';
import { extractErrorAttributes } from '@subscriptions/application/utils';
import type { PaymentMethodTypeType } from '@subscriptions/domain/types/SubscriptionsTypeSafe';

const paymentLog = logger.forDomain('SUB', { subdomain: 'billing' });
const tracer = trace.getTracer('@parrot-software/observability');

/** Only usable saved methods are surfaced to the selection step. */
const ACTIVE_PAYMENT_METHOD_STATUS = 'ACTIVE';

/**
 * Use case for listing the organization's saved payment methods
 * (`GET /v1/payment-methods?type=…`). The org is derived server-side from the
 * Bearer token — no extra parameters are sent.
 *
 * Defensive filtering: only methods with `status === 'ACTIVE'` are returned,
 * so the saved-cards selection step never offers an unusable method.
 *
 * Never logs `providerId` / `providerPublicId` / card digits (charter §5) —
 * only the method type and the resulting count.
 *
 * @param port - Payment method API port
 * @returns Use case executor
 */
export const listPaymentMethodsUseCase = (port: IPaymentMethodApiPort) => {
  const execute = async (type: PaymentMethodTypeType): Promise<PaymentMethodViewDto[]> => {
    const span = tracer.startSpan('SUB.listSavedPaymentMethods', { attributes: { methodType: type } });
    const ctx = trace.setSpan(context.active(), span);

    return context.with(ctx, async () => {
      try {
        const paymentMethods = await port.listPaymentMethods(type);
        const activeMethods = paymentMethods.filter((method) => method.status === ACTIVE_PAYMENT_METHOD_STATUS);
        paymentLog.info('listSavedPaymentMethods', { methodType: type, cardsCount: activeMethods.length });
        return activeMethods;
      } catch (error) {
        paymentLog.error('listSavedPaymentMethods', { methodType: type, ...extractErrorAttributes(error) });
        throw error;
      } finally {
        span.end();
      }
    });
  };

  return { execute };
};
